import { useEffect, useRef, useState } from 'react';
import { HEADER_GRID_CLASSNAME } from '../constants';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { startOfDay } from 'date-fns';
import { Button } from '@components/ui/button';
import { DatePicker } from '@components/ui/form/date';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { MAX_MULTI_DAYS } from '@forms/schedules/internal/constants';
import { InternalScheduleFormValues } from '@forms/schedules/internal/types';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { longDay, shortDay, shortDayDate } from '@utils/i18n';
import { cn } from '@utils/cn';
import { InternalScheduleMultiDayInputsProps, MultiDayField } from './types';
import { countWorkdays, getDurationUnit, getEndDate, getTurnedOffStartDay } from './helpers';

const FLASH_DURATION = 500;
const SATURDAY = 6;
const SUNDAY = 0;

export const InternalScheduleMultiDayInputs = ({
  startDate,
  disabled,
  weekendDefaults,
  dateControl,
  dateCaption,
  popoverRef,
}: InternalScheduleMultiDayInputsProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'schedule.multiDay' });
  const { setValue, watch } = useFormContext<InternalScheduleFormValues>();
  const { setState: setSidebarState } = useSchedulerSidebar();
  const [days, skipSaturday, skipSunday] = watch(['days', 'skipSaturday', 'skipSunday']);
  const options = { skipSaturday, skipSunday };
  const [daysInput, setDaysInput] = useState(String(days));
  const [endDate, setEndDate] = useState(() => getEndDate(startDate, days, options));
  const [lastEdited, setLastEdited] = useState<MultiDayField>('days');
  const [flash, setFlash] = useState<MultiDayField | null>(null);
  const [turnedOnDay, setTurnedOnDay] = useState<Date | null>(null);
  const flashTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const lastStart = useRef(startDate.getTime());

  const flashField = (field: MultiDayField) => {
    clearTimeout(flashTimeout.current);
    setFlash(field);
    flashTimeout.current = setTimeout(() => setFlash(null), FLASH_DURATION);
  };

  const applyDays = (value: number, nextOptions = options) => {
    const nextDays = Math.min(Math.max(value, 1), MAX_MULTI_DAYS);
    setValue('days', nextDays);
    setDaysInput(String(nextDays));
    const nextEnd = getEndDate(startDate, nextDays, nextOptions);
    if (nextEnd.getTime() !== endDate.getTime()) flashField('end');
    setEndDate(nextEnd);
  };

  const applyEndDate = (value: Date, nextOptions = options) => {
    const nextDays = countWorkdays(startDate, value, nextOptions);
    setEndDate(startOfDay(value));
    if (nextDays !== days) flashField('days');
    setValue('days', nextDays);
    setDaysInput(String(nextDays));
  };

  useEffect(() => {
    setValue('skipSaturday', weekendDefaults.skipSaturday);
    setValue('skipSunday', weekendDefaults.skipSunday);
  }, [weekendDefaults.skipSaturday, weekendDefaults.skipSunday, setValue]);

  useEffect(() => () => clearTimeout(flashTimeout.current), []);

  useEffect(() => {
    setSidebarState?.((prev) => ({ ...prev, plannedDays: days }));
  }, [days, setSidebarState]);

  useEffect(() => {
    const turnedOff = getTurnedOffStartDay(startDate, options);
    const nextOptions = turnedOff ? { ...options, [turnedOff]: false } : options;
    const startChanged = lastStart.current !== startDate.getTime();
    lastStart.current = startDate.getTime();
    if (turnedOff) {
      setValue(turnedOff, false);
      setTurnedOnDay(startDate);
    } else if (startChanged) setTurnedOnDay(null);
    if (lastEdited === 'end' && !(endDate < startOfDay(startDate)))
      applyEndDate(endDate, nextOptions);
    else applyDays(days, nextOptions);
  }, [startDate.getTime(), skipSaturday, skipSunday]);

  const toggleWeekend = (field: 'skipSaturday' | 'skipSunday') => {
    setLastEdited('days');
    setValue(field, !options[field]);
  };

  const unit = getDurationUnit(days, options);
  const disabledWeekdays = [...(skipSaturday ? [SATURDAY] : []), ...(skipSunday ? [SUNDAY] : [])];

  return (
    <div className={HEADER_GRID_CLASSNAME}>
      {dateControl}
      <div className="flex items-center gap-2 text-sm text-neutral-700">
        <span>{t('for')}</span>
        <div
          className={cn(
            'flex h-8 items-center rounded border border-neutral-300 bg-white transition-colors duration-500',
            flash === 'days' && 'bg-primary-50',
          )}>
          <Button
            variant="transparent"
            size="xs"
            className="h-full w-7 px-0 text-sm"
            disabled={disabled || days <= 1}
            aria-label={t('decrease')}
            onClick={() => {
              setLastEdited('days');
              applyDays(days - 1);
            }}>
            <SvgConsumer id="minus" />
          </Button>
          <input
            type="text"
            inputMode="numeric"
            aria-label={t(`unit.${unit}`)}
            className="h-full w-10 bg-transparent text-center text-sm font-semibold outline-none"
            value={daysInput}
            disabled={disabled}
            onChange={(e) => {
              const digits = e.target.value.replace(/[^0-9]/g, '');
              setDaysInput(digits);
              const value = Number(digits);
              if (!value) return;
              setLastEdited('days');
              applyDays(value);
            }}
            onBlur={() => {
              if (!Number(daysInput)) applyDays(1);
              else setDaysInput(String(days));
            }}
          />
          <Button
            variant="transparent"
            size="xs"
            className="h-full w-7 px-0 text-sm"
            disabled={disabled || days >= MAX_MULTI_DAYS}
            aria-label={t('increase')}
            onClick={() => {
              setLastEdited('days');
              applyDays(days + 1);
            }}>
            <SvgConsumer id="plus" />
          </Button>
        </div>
        <span>{t(`unit.${unit}`)}</span>
        <span className="text-neutral-400">→</span>
        <span>{t('ends')}</span>
        <DatePicker
          mode="single"
          value={endDate}
          minDate={startOfDay(startDate)}
          disabledDays={disabledWeekdays.length ? [{ dayOfWeek: disabledWeekdays }] : []}
          disabled={disabled}
          popoverRef={popoverRef}
          onChange={(value) => {
            if (!value) return;
            setLastEdited('end');
            applyEndDate(value);
          }}>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              'flex h-8 items-center gap-2 whitespace-nowrap rounded border border-neutral-300 bg-white px-3 text-sm font-semibold text-neutral-800 transition-colors duration-500 disabled:cursor-not-allowed disabled:opacity-50',
              flash === 'end' && 'bg-primary-50',
            )}>
            {shortDayDate(endDate)}
            <SvgConsumer id="calendar" className="text-neutral-400" />
          </button>
        </DatePicker>
      </div>
      <div className="flex justify-center">{dateCaption}</div>
      <div className="flex min-h-6 flex-wrap items-center gap-2 text-xs text-neutral-600">
        {days > 1 && (
          <>
            <span>{t('workWeekends')}</span>
            {(['skipSaturday', 'skipSunday'] as const).map((field) => (
              <button
                key={field}
                type="button"
                aria-pressed={!options[field]}
                disabled={disabled}
                onClick={() => toggleWeekend(field)}
                className={cn(
                  'rounded-full border px-2.5 py-0.5 font-semibold transition-colors',
                  options[field]
                    ? 'border-neutral-300 bg-white text-neutral-500 hover:border-neutral-400'
                    : 'border-primary-500 bg-primary-50 text-primary-700',
                )}>
                {t(field === 'skipSaturday' ? 'saturday' : 'sunday')}
              </button>
            ))}
            {turnedOnDay && (
              <span className="text-neutral-500">
                {t('startDayTurnedOn', { day: longDay(turnedOnDay), short: shortDay(turnedOnDay) })}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
};
